/**
 * AI CONTEXT
 * Purpose: reusable attendance row for a student/assistant/exception item.
 * Features: identity + tags + notes + attendance status selector + optional student modal.
 * Used by: attendance lists in admin and teacher views.
 * Importance: primary row-level attendance interaction component.
 * Refactor: split display mapping and modal behavior into helper hooks/components.
 */
import { FlagFilled, FolderOpenOutlined } from "@ant-design/icons"
import { Tooltip } from "antd"
import { forwardRef, type ReactNode, useMemo, useState } from "react"
import styled from "styled-components"

import AnimateInFlex, { ANIMATION_BY_KEY } from "#blocks/AnimateInFlex"
import Flex from "#blocks/Flex"
import LinkButton from "#blocks/LinkButton"
import TagIcons from "#blocks/TagIcons"
import TextOverrun from "#blocks/TextOverrun"
import Log from "#classes/Log"
import { Student } from "#classes/Student"
import { AttendanceStatusSelectorBar } from "#components/AttendanceStatusSelectorBar"
import HoverableNote from "#components/HoverableNote"
import { CLASS_GROUPS } from "#constants/classOptions"
import { ATTENDANCE_STATUSES } from "#constants/globalConstants"
import { useAdminViewContext } from "#contexts/AdminViewContextProvider"
import { useThemeContext } from "#contexts/ThemeContextProvider"
import { StudentTask } from "#customTypes/composedTypes/studentTaskTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { AttendanceStatus, invariant } from "#customTypes/types"
import AddStudentFormModal from "#forms/AddStudent/AddStudentFormModal"
import { COLORS } from "#styles/colors"
import { getIsTaskLineOpen } from "#utils/taskHelpers"
import SimplifiedStudentProfileModal from "#views/TeacherView/SimplifiedStudentProfileModal"

import AttendanceRowTaskLine from "./AttendanceRowTaskLine"

const HIDE_AVATAR_TAG_COUNT = 4

/** Light amber tint behind rows whose student still has open tasks. */
const OPEN_TASK_ROW_BACKGROUND = "#FFF7E6"
const NO_OPEN_TASKS: StudentTask[] = []

const SFlex = styled(Flex)`
  flex-grow: 1;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
`

const SRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const STaskFlag = styled.div<{ $isLineOpen: boolean }>`
  cursor: pointer;
  transition: transform 0.25s ease-in-out;
  color: ${COLORS.priority.SOON};
  /* Tight dark edge so the amber flag reads against the amber row tint.
     Constant across open/closed — the rotation alone signals the state. */
  filter: drop-shadow(0 1px 0.5px rgba(0, 0, 0, 0.55));
  display: flex;
  align-items: center;
  transform: ${({ $isLineOpen }) => ($isLineOpen ? "rotate(-30deg)" : "none")};

  &:hover {
    transform: ${({ $isLineOpen }) =>
      $isLineOpen ? "rotate(-30deg) scale(1.3)" : "scale(1.3)"};
  }
`

/**
 * Flag icon shown next to the student name while they have open tasks, so the
 * front desk sees the task where they are already looking (the roster) rather
 * than only in the task panel off to the right (JARVIS-112). Task details live
 * in the task line's popover; clicking the flag toggles the line open/closed
 * regardless of attendance status (open early to peek at tasks before the
 * student arrives). Tilted while the line is open, upright while closed.
 */
const OpenTaskFlag = ({
  openTasks,
  isLineOpen,
  onToggle,
}: {
  openTasks: StudentTask[]
  isLineOpen: boolean
  onToggle: () => void
}) => {
  if (openTasks.length === 0) return null

  return (
    <STaskFlag
      data-testid="attendance-row-task-flag"
      $isLineOpen={isLineOpen}
      onClick={(e) => {
        e.stopPropagation()
        onToggle()
      }}
    >
      <FlagFilled />
    </STaskFlag>
  )
}

const STYLE_BY_SIZE = {
  small: {
    height: 30,
    fontSize: 13,
    avatarSize: 15,
    gap: 10,
  },
  large: {
    height: 45,
    fontSize: 16,
    avatarSize: 28,
    gap: 20,
  },
}

const StudentNotes = ({ notes }: { notes: string | undefined }) => {
  const { eric } = useThemeContext()

  return notes ? (
    <AnimateInFlex
      animation={ANIMATION_BY_KEY.down}
      style={{
        fontSize: 12,
        width: "100%",
        background: `linear-gradient(to bottom, ${eric.red}75, ${eric.red}99)`,
        color: "white",
        padding: "4px 10px",
        borderRadius: "0px 0px 4px 4px",
        marginTop: 4,
      }}
    >
      <TextOverrun text={notes} />
    </AnimateInFlex>
  ) : null
}

const AttendanceRow = forwardRef<
  HTMLDivElement,
  {
    student: StudentConstructor
    status: AttendanceStatus
    sessionId: Uuid
    classGroup: CLASS_GROUPS
    onStatusChange: (
      student: StudentConstructor,
      status: AttendanceStatus,
      sessionId: Uuid
    ) => void
    size?: "small" | "large"
    onStudentClick?: () => void
    simplifiedProfile?: boolean
    onOpenPortfolio?: (studentId: Uuid) => void
    /** e.g. missed session day/time/project for teacher makeup rows */
    subtitle?: ReactNode
    /**
     * Student's uncompleted tasks. When non-empty the row is tinted and shows a
     * flag next to the name. Wrappers decide whether to pass this (front desk
     * does; teacher view does not).
     */
    openTasks?: StudentTask[]
  }
>(
  (
    {
      student,
      status,
      sessionId,
      classGroup,
      onStatusChange,
      size = "small",
      onStudentClick,
      simplifiedProfile = false,
      onOpenPortfolio,
      subtitle,
      openTasks = NO_OPEN_TASKS,
    },
    ref
  ) => {
    const hasOpenTasks = openTasks.length > 0
    const [showModal, setShowModal] = useState(false)
    const { eric } = useThemeContext()
    // AdminViewContextProvider wraps the whole app, so this is safe in teacher
    // views too (where openTasks is empty and no flag or line renders).
    const { taskLineToggles, setTaskLineOpen } = useAdminViewContext()
    const isTaskLineOpen = getIsTaskLineOpen({
      status,
      tasks: openTasks,
      manualOpen: taskLineToggles[student.id],
    })

    const { height, fontSize, avatarSize, gap } = useMemo(() => {
      const style = { ...STYLE_BY_SIZE[size] }

      if (student.notes) style.height += 30

      return style
    }, [size, student.notes])

    const handleStudentClick = () => {
      if (onStudentClick) {
        onStudentClick()
      } else {
        setShowModal(true)
      }
    }

    const studentForDisplay = useMemo(() => {
      const studentObject = new Student(student)
      const displayData = studentObject.getStudentForDisplay()
      const tagCount = studentObject.tags?.length ?? 0
      const allergiesCount = studentObject.allergies?.length ?? 0
      const showAvatar = tagCount + allergiesCount < HIDE_AVATAR_TAG_COUNT

      invariant(
        displayData != null,
        `${Log.student(student)} AttendanceRow data is invalid`
      )

      return {
        avatar:
          displayData.avatar ??
          `https://placehold.co/${avatarSize}x${avatarSize}`,
        name: displayData.name,
        dob: displayData.dob,
        skillLevel:
          classGroup === CLASS_GROUPS.DIGITAL_ART
            ? displayData.digitalSkillLevel
            : classGroup === CLASS_GROUPS.SCULPTURE
              ? displayData.sculptureSkillLevel
              : // CORE, PAINTING, PORTFOLIO — portfolio uses core ladder (ALL_SKILL_LEVELS)
                displayData.coreSkillLevel,
        tags: studentObject.tags,
        allergies: studentObject.allergies,
        showAvatar,
        notes: studentObject.notes,
      }
    }, [student, classGroup, avatarSize])

    const {
      name,
      dob,
      skillLevel,
      tags,
      allergies,
      showAvatar,
      notes,
      avatar,
    } = studentForDisplay

    return (
      <SRowWrapper
        data-testid={`attendance-row-${student.id}`}
        data-has-open-tasks={hasOpenTasks}
      >
        <AnimateInFlex
          ref={ref}
          animation={ANIMATION_BY_KEY.leftSm}
          style={{
            justifyContent: "space-between",
            alignItems: "center",
            padding: "2px 4px",
            height,
            fontSize,
            width: "100%",
            ...(hasOpenTasks
              ? {
                  background: OPEN_TASK_ROW_BACKGROUND,
                  borderLeft: `3px solid ${COLORS.priority.SOON}`,
                  borderRadius: 4,
                }
              : {}),
          }}
          col
        >
          <Flex style={{ justifyContent: "space-between", width: "100%" }}>
            <SFlex gap={gap}>
              {showAvatar && (
                <img
                  src={avatar}
                  style={{
                    width: avatarSize,
                    height: avatarSize,
                    borderRadius: "50%",
                  }}
                />
              )}

              <Flex>{dob}</Flex>
              <LinkButton
                style={{
                  minWidth: 0,
                  flexGrow: 1,
                  display: "flex",
                  justifyContent: "flex-start",
                }}
                onClick={handleStudentClick}
              >
                <TextOverrun text={`${name}`} />
              </LinkButton>
              {hasOpenTasks && (
                <Flex center style={{ width: 20, flexShrink: 0 }}>
                  <OpenTaskFlag
                    openTasks={openTasks}
                    isLineOpen={isTaskLineOpen}
                    onToggle={() =>
                      setTaskLineOpen(student.id, !isTaskLineOpen)
                    }
                  />
                </Flex>
              )}
              {notes && (
                <Flex center style={{ width: 20, flexShrink: 0 }}>
                  <HoverableNote note={notes} />
                </Flex>
              )}
              {(allergies?.length || tags?.length) && (
                <Flex
                  style={{
                    flexShrink: 0,
                    maxWidth: "fit-content",
                  }}
                >
                  <TagIcons values={allergies} />
                  <TagIcons values={tags} />
                </Flex>
              )}
            </SFlex>

            <Flex
              style={{
                alignItems: "center",
                justifyContent: "flex-end",
                flexShrink: 0,
                gap: 4,
              }}
            >
              {onOpenPortfolio && (
                <Tooltip title="View portfolio">
                  <FolderOpenOutlined
                    onClick={(e) => {
                      e.stopPropagation()
                      onOpenPortfolio(student.id)
                    }}
                    style={{
                      fontSize: 15,
                      cursor: "pointer",
                      color: "#722ed1",
                    }}
                  />
                </Tooltip>
              )}
              <Flex center style={{ padding: 0, width: 30 }}>
                <Flex style={{ width: 20, padding: "auto" }}>{skillLevel}</Flex>
              </Flex>
            </Flex>

            <AttendanceStatusSelectorBar
              status={status}
              onStatusChange={(newStatus) => {
                onStatusChange(student, newStatus, sessionId)
              }}
              size={size}
              disabled={status === ATTENDANCE_STATUSES.PLANNED_ABSENCE}
            />
          </Flex>
          <StudentNotes notes={notes} />
        </AnimateInFlex>
        {/* Gated on the openTasks prop: wrappers decide whether tasks show
            here (front desk passes them; teacher view does not). */}
        {hasOpenTasks && (
          <AttendanceRowTaskLine student={student} status={status} />
        )}
        {subtitle ? (
          <Flex
            style={{
              padding: "0 4px 4px",
              fontSize: Math.max(10, fontSize - 5),
              color: eric.gray,
              fontWeight: 500,
              minWidth: 0,
              width: "100%",
            }}
          >
            {typeof subtitle === "string" ? (
              <TextOverrun text={subtitle} />
            ) : (
              subtitle
            )}
          </Flex>
        ) : null}
        {showModal ? (
          simplifiedProfile ? (
            <SimplifiedStudentProfileModal
              onClose={() => setShowModal(false)}
              studentId={student.id}
            />
          ) : (
            <AddStudentFormModal
              onClose={() => setShowModal(false)}
              studentId={student.id}
            />
          )
        ) : null}
      </SRowWrapper>
    )
  }
)

export default AttendanceRow
